/**
 * Criar minha família.
 *
 * Quem cria entra automaticamente — o nome da tela já diz de quem é a família.
 * As outras pessoas são **convidadas**, não adicionadas: a nota explica isso
 * antes de alguém clicar, porque a diferença importa (estar numa família muda
 * como as despesas são divididas).
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { Card, SectionLabel } from '../../ui/primitives';
import type { MembroDaViagem } from '../../trip/TripContext';
import { Field } from '../auth/Field';
import { createFamily, GrupoError } from './api';
import { FotoAvatar } from '../perfil/FotoAvatar';
import { usePessoas } from '../roteiro/people';

export function CriarFamiliaScreen({
  tripId,
  membros,
  meuMemberId,
}: {
  tripId: string;
  membros: MembroDaViagem[];
  meuMemberId: string;
}) {
  const { fotoDe } = usePessoas();
  const navegar = useNavigate();
  const cliente = useQueryClient();
  const [nome, setNome] = useState('');
  const [convidados, setConvidados] = useState<string[]>([]);

  const criar = useMutation({
    mutationFn: () =>
      createFamily(tripId, { name: nome.trim(), inviteMemberIds: convidados }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['grupo', tripId] });
      void cliente.invalidateQueries({ queryKey: ['viagem'] });
      navegar('/grupo', { replace: true });
    },
  });

  // Quem já está numa família não pode ser convidado: uma família ativa por
  // pessoa por viagem. Mostrar e falhar depois seria pior do que não mostrar.
  const disponiveis = membros.filter(
    (m) => m.memberId !== meuMemberId && !m.familyId,
  );
  const jaEmFamilia = membros.filter((m) => m.memberId !== meuMemberId && m.familyId);

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    if (nome.trim().length === 0) return;
    criar.mutate();
  }

  return (
    <section className="grupo">
      <header className="fin-nova__header">
        <h1 className="grupo__titulo">Criar minha família</h1>
      </header>

      <form className="auth-form" onSubmit={enviar}>
        <Field
          label="Nome da família"
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          placeholder="Família Silva"
          required
        />

        <SectionLabel>CONVIDAR</SectionLabel>
        <Card variant="surf">
          {disponiveis.length === 0 ? (
            <p className="auth-note">
              Ninguém disponível para convidar agora — todo mundo já está em uma
              família.
            </p>
          ) : (
            disponiveis.map((m) => {
              const marcado = convidados.includes(m.memberId);
              return (
                <label className="fin-nova__pessoa" key={m.memberId}>
                  <input
                    type="checkbox"
                    checked={marcado}
                    onChange={() =>
                      setConvidados((atual) =>
                        marcado
                          ? atual.filter((id) => id !== m.memberId)
                          : [...atual, m.memberId],
                      )
                    }
                  />
                  <FotoAvatar nome={m.name} url={fotoDe(m.memberId).url} cor={fotoDe(m.memberId).cor} tamanho={24} />
                  <span>{m.name}</span>
                </label>
              );
            })
          )}
        </Card>

        <Card variant="chip">
          <p className="auth-note">
            Cada pessoa pode estar em uma família por viagem. Quem você convidar
            recebe um convite e decide — ninguém entra sem aceitar.
            {jaEmFamilia.length > 0 && (
              <>
                {' '}
                {jaEmFamilia.length === 1
                  ? '1 pessoa já está em outra família e não aparece na lista.'
                  : `${jaEmFamilia.length} pessoas já estão em outras famílias e não aparecem na lista.`}
              </>
            )}
          </p>
        </Card>

        <Card variant="chip">
          <p className="auth-note">
            Com a família criada, a viagem passa a ter visão financeira familiar
            e a divisão de despesa por família.
          </p>
        </Card>

        {criar.isError && (
          <p className="auth-error" role="alert">
            {(criar.error as GrupoError).message}
          </p>
        )}

        <button
          type="submit"
          className="nv-btn nv-btn--primary nv-btn--block"
          disabled={nome.trim().length === 0 || criar.isPending}
        >
          {criar.isPending
            ? 'Criando…'
            : convidados.length > 0
              ? 'Criar família e convidar'
              : 'Criar família'}
        </button>
      </form>
    </section>
  );
}
